import React from 'react';
import { AppTask } from '@domain/tasks/models/Task';
import { APP_CONFIG } from '../../../../config/app.config';
import { taskIcon, taskIconTone } from './utils';
import { Box, Typography } from '@mui/material';
import { Icon, IconSize } from '@shared/icons';

interface TaskItemProps {
  task: AppTask;
  onClick?: (task: AppTask) => void;
}

const toneColor = (tone: ReturnType<typeof taskIconTone>): string => {
  const colors = APP_CONFIG.ui.theme.colors;
  switch (tone) {
    case 'error':
      return colors.error;
    case 'warning':
      return colors.warning;
    case 'secondary':
      return colors.secondary;
    default:
      return colors.primary;
  }
};

const TaskItem: React.FC<TaskItemProps> = ({ task, onClick }) => {
  const colors = APP_CONFIG.ui.theme.colors;

  return (
    <Box
      onClick={() => onClick?.(task)}
      sx={{
        bgcolor: colors.background,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { bgcolor: colors.backgroundTint } : undefined,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '8px',
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: toneColor(taskIconTone(task.type)),
            opacity: 0.1,
          }}
        />
        <Box sx={{ position: 'relative', display: 'flex', lineHeight: 0 }}>
          <Icon icon={taskIcon(task.type)} size={IconSize.MEDIUM} color={toneColor(taskIconTone(task.type))} />
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" fontWeight={500} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {task.title}
        </Typography>
        {task.subtitle && (
          <Typography variant="caption" color="text.secondary" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            {task.subtitle}
          </Typography>
        )}
      </Box>
      {onClick && (
        <Box sx={{ opacity: 0.6, display: 'flex', flexShrink: 0 }}>
          <Icon icon="ChevronRight" size={IconSize.SMALL} color={colors.primaryText} />
        </Box>
      )}
    </Box>
  )
};

export default TaskItem;
